/* eslint-disable prettier/prettier */
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from 'app/components/ui/accordion';
import { useCourseDetailsSlice } from '../slice';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import Layout from '../components/layout';
import { Button } from 'app/components/ui/button';
import { MonitorPlay } from 'lucide-react';
import { formatDuration } from 'date-fns';
import DialogVideoPlayer from '../components/video-play';

export default function CourseContent() {
  const { useLazyGetCourseContentQuery, useLecturePreviewMutation } =
    useCourseDetailsSlice();
  const [getCourseContent, { data: content, error, isLoading }] =
    useLazyGetCourseContentQuery();
  const [open, setOpen] = useState(false);

  const [lecturePreview, { isLoading: loadingVideo, data: lecture }] =
    useLecturePreviewMutation();

  const { courseId } = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    if (courseId) {
      getCourseContent(courseId);
    }
  }, [courseId]);

  const handlePreview = contentId => {
    if (courseId) {
      lecturePreview({ content_id: contentId, course_id: +courseId });
    }
  };

  useEffect(() => {
    if (lecture) {
      setOpen(true);
    }
  }, [dispatch, lecture]);

  return (
    <div>
      <Layout>
        <div>
          <div className="flex justify-between items-start mb-6">
            {/* Left Section */}
            <div>
              <h2 className="text-2xl font-bold">Course content</h2>
              <p className="text-sm text-gray-600 mt-1">
                {content?.data?.topics?.length} sections •{' '}
                {content?.data?.course_metrics?.total_videos} lectures •{' '}
                {formatDuration(
                  content?.data?.course_metrics?.total_duration || '',
                )}{' '}
                total length
              </p>
            </div>
          </div>

          {/* Accordion */}
          <div className="border border-gray-200 rounded-lg">
            <Accordion type="multiple" defaultValue={`section-0`}>
              {content?.data?.topics?.map((section, index) => (
                <AccordionItem key={index} value={`section-${index}`}>
                  <AccordionTrigger className="w-full py-3 px-4 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <div className="flex w-full justify-between">
                      <div>
                        <span className="font-medium text-black text-[15px] truncate">
                          {section?.topic_name}
                        </span>
                        <span className="text-[13px] text-gray-500 ml-auto text-right w-36">
                          {section?.description}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-500 text-[13px]">
                        <span>{section?.contents?.length} lectures</span>
                        <span className="bg-gray-500 rounded-full w-1 h-1 mx-2 inline-flex items-center "></span>
                        <span>{section?.total_duration}</span>
                      </div>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="py-4 px-7">
                    <div>
                      {section?.contents?.map((lesson, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between py-3 border-b border-gray-200 last:border-0 last:pb-0"
                        >
                          <div className="flex items-center gap-2 text-gray-800 text-[13px]">
                            <span>
                              <MonitorPlay size={16} />
                            </span>
                            <span className="font-medium">
                              {lesson?.description}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-[13px]">
                            <span
                              className="text-primary cursor-pointer hover:text-underline"
                              onClick={() => handlePreview(lesson?.content_id)}
                            >
                              Preview
                            </span>
                            <span className="text-gray-500 ">
                              {lesson?.video_duration}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        {/* Video Player Dialog */}
        {lecture && (
          <DialogVideoPlayer
            isOpen={open}
            setIsOpen={setOpen}
            file={lecture?.data}
          />
        )}
      </Layout>
    </div>
  );
}
